import type {
  RetirementEpisodeStateInfo, RetirementEpisodes, RetirementEpisodesQuery,
} from '../types/retirementEpisodes'
import { MAX_EPISODE_REPORT_IDS } from '../types/retirementEpisodes'
import { fetchApi } from './api'

/**
 * Retirement episodes — ObservatoryRetirementEpisodesController, LighthouseAdmin at
 * class level, GET only.
 *
 * Through the shared `fetchApi`, so a failure arrives as an ApiError carrying its
 * status: the section tells a 403 (RestrictedState) from a 500 (the table's
 * migration not applied) from an empty table, and it can only do that by status.
 */

const BASE = '/api/observatory/retirement/episodes'

/**
 * The episodes list's query, narrowed to one report when `reportId` is given.
 *
 * Report Detail's Governance tab asks for one report's episodes with it. The
 * server narrows `rows` and `totalCount` to that report and keeps `summary` (and
 * the lock sentence) whole-table, so a caller must not read the summary as this
 * report's. A report with no episode is an empty page, not a 404; a malformed id
 * is the server's 400. Absent or null sends no parameter: every report.
 */
export interface RetirementEpisodesRequest extends RetirementEpisodesQuery {
  reportId?: string | null
}

export function getRetirementEpisodes(query: RetirementEpisodesRequest): Promise<RetirementEpisodes> {
  const params = new URLSearchParams()
  if (query.state) params.set('state', query.state)
  if (query.skip != null) params.set('skip', String(query.skip))
  if (query.top != null) params.set('top', String(query.top))
  if (query.reportId) params.set('reportId', query.reportId)

  const qs = params.toString()
  return fetchApi<RetirementEpisodes>(`${BASE}${qs ? `?${qs}` : ''}`)
}

/**
 * The latest episode of each report, as repeated `reportIds=` parameters.
 *
 * Refuses more than the server's cap here too, before any request: the server
 * answers 400 above 50 ids (a request line of 200 GUIDs would pass Kestrel's 8 KB
 * default), and a caller slicing silently would read the missing reports as
 * "never on the clock".
 */
export function getEpisodeStates(reportIds: string[]): Promise<RetirementEpisodeStateInfo[]> {
  if (reportIds.length > MAX_EPISODE_REPORT_IDS) {
    return Promise.reject(new Error(
      `At most ${MAX_EPISODE_REPORT_IDS} report ids per request; ${reportIds.length} were asked for.`))
  }

  const params = new URLSearchParams()
  for (const id of reportIds) params.append('reportIds', id)
  return fetchApi<RetirementEpisodeStateInfo[]>(`${BASE}/by-report?${params.toString()}`)
}
